import { AuthFetchError } from '@/lib/api'

/**
 * Map the AuthFetchError envelope the Worker returns from an undo call
 * (`{ error, entity? }`) onto the right translated message — expired /
 * already_undone / has_dependents / not_found / not_undoable / generic — so
 * every surface that can undo an Eric action (the agentic result banner AND the
 * thread transcript's ResultCard) renders the same friendly reason instead of a
 * raw `{"error":"expired"}` JSON string.
 *
 * Keys live under the `dashboard` namespace (`dashboard.agentic.undo.error.*`);
 * pass a `t` already bound to that namespace.
 */
/**
 * Raw undo error code for telemetry (DESIGN.md §9: IDs and enums only —
 * never the translated sentence). Falls back to 'generic' so PostHog
 * cardinality stays bounded.
 */
export function undoErrorCode(err: unknown): string {
  if (err instanceof AuthFetchError) {
    const body = err.body as { error?: unknown } | null
    if (body && typeof body.error === 'string') return body.error
  }
  return 'generic'
}

export function mapUndoError(
  err: unknown,
  t: (key: string, opts?: Record<string, unknown>) => string,
): string {
  // Translate the `entity` discriminator the Worker returns ('customer' /
  // 'vehicle' / 'appointment') into a localized noun; fall back to a generic
  // "record". Shared by the entity-aware error strings below.
  const entityLabel = (entity: unknown): string => {
    const code = typeof entity === 'string' ? entity : null
    return code === 'customer'
      ? t('agentic.undo.entity.customer')
      : code === 'vehicle'
        ? t('agentic.undo.entity.vehicle')
        : code === 'appointment'
          ? t('agentic.undo.entity.appointment')
          : t('agentic.undo.entity.record')
  }
  if (err instanceof AuthFetchError) {
    const body = err.body as { error?: unknown; entity?: unknown } | null
    const code = body && typeof body.error === 'string' ? body.error : null
    if (code === 'expired') return t('agentic.undo.error.expired')
    if (code === 'already_undone') return t('agentic.undo.error.already_undone')
    // `superseded` = the entity (appointment / customer / vehicle) was modified
    // after Eric's action, so undoing it would clobber that change — refuse
    // rather than overwrite. Entity-aware: the gate now fires for field/notes
    // restores on customers + vehicles too, not just appointments.
    if (code === 'superseded')
      return t('agentic.undo.error.superseded', { entity: entityLabel(body?.entity) })
    if (code === 'has_dependents') {
      return t('agentic.undo.error.has_dependents', { entity: entityLabel(body?.entity) })
    }
    // `not_found` = the assistant_requests row is gone (route 404);
    // `target_not_found` = applyUndo found the row but the customer/vehicle/
    // appointment it points at was deleted within the undo window. Both read
    // as "record not found" to the user.
    if (code === 'not_found' || code === 'target_not_found')
      return t('agentic.undo.error.not_found')
    if (code === 'not_undoable') return t('agentic.undo.error.not_undoable')
  }
  return t('agentic.undo.error.generic')
}
